import { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';

import { useLoginStore } from '@savgroup-front-common/core/domains/login/useLoginStore';
import { useSearchParams } from '@savgroup-front-common/core/hooks';
import { handleSignInRedirectCallback } from '@savgroup-front-common/core/services/userManager';

import { logError } from '../../../../../configuration';
import { signIn } from '../../../../../domains/login/actions';
import { useAppContext } from '../../../App.context';

const NOT_REGISTERED_USER_LOGOUT_DELAY_IN_MS = 60_000;
const RELOAD_DELAY_IN_MS = 5_000;

export const useOAuthServer = () => {
  const dispatch = useDispatch();

  const query = useSearchParams();
  const state = query.get('state');

  const { pathname, search: urlSearch } = useLocation();

  const navigate = useNavigate();
  const { user, login, logout } = useAppContext();
  const setLoginStore = useLoginStore((store) => store.setLoginStore);
  const [isNotRegisteredUser, setIsNotRegisteredUser] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        if (!user && state) {
          await handleSignInRedirectCallback(window.location.href);

          return;
        }

        const urlBeforeRedirection = (user?.state as { redirectUrl?: string })
          ?.redirectUrl;

        if (urlBeforeRedirection && !urlBeforeRedirection.startsWith('/')) {
          throw new Error('Invalid redirect URL');
        }

        const payload = {
          onLogin: () => navigate(urlBeforeRedirection ?? '/'),
          onGetUserForbidden: () => {
            setIsNotRegisteredUser(true);

            setTimeout(() => {
              logout();
            }, NOT_REGISTERED_USER_LOGOUT_DELAY_IN_MS);
          },
          setLoginStore,
        };

        dispatch(signIn(payload) as any);
      } catch (e) {
        logError(e);

        if (
          e instanceof Error &&
          e.message === 'No matching state found in storage'
        ) {
          login({
            returnTo: `${pathname}${urlSearch}`,
          });
        }
      }
    })();
  }, [
    dispatch,
    login,
    logout,
    navigate,
    pathname,
    urlSearch,
    state,
    user,
    setLoginStore,
  ]);

  useEffect(() => {
    if (isNotRegisteredUser || user) {
      return undefined;
    }

    const timeoutId = window.setTimeout(() => {
      window.location.reload();
    }, RELOAD_DELAY_IN_MS);

    return () => {
      window.clearTimeout(timeoutId);
    };
  }, [isNotRegisteredUser, user]);

  return { isNotRegisteredUser };
};
